import React from "react";
import {
	Button,
	ClassNameProps,
	Column,
	Conditional,
	Else,
	Heading,
	If,
	isAfter,
	makeClassName,
	Modal,
	ModalProps,
	Row,
	StyleProps,
	useSync,
} from "@hex-insights/core";
import { Icon } from "../../../../Components/Icon";
import { ViewRecorder } from "../../../../Components/ViewRecorder";
import { CalendarEvent, useCalendarEventCalendarDetailQuery } from "../../../../Utilities";
import { CalendarEventUtilities } from "../../../Utilities";
import { CalendarEventDetailDisplay, DetailDisplayCalendarEvent } from "../DetailDisplay";
import { RSVPForm } from "../RSVPForm";
import styles from "./styles.module.css";

export type ModalCalendarEvent = DetailDisplayCalendarEvent;

export type ControlledCalendarEventModalProps = Omit<CalendarEventModalProps, "isLoading" | "calendarEvent"> & {
	calendarEventID: CalendarEvent["id"];
};

export function ControlledCalendarEventModal({ calendarEventID, ...props }: ControlledCalendarEventModalProps) {
	const { loading, data } = useCalendarEventCalendarDetailQuery({ variables: { id: calendarEventID } });
	const calendarEvent = data?.calendarEventConnection?.edges[0]?.node;

	return <CalendarEventModal isLoading={loading} calendarEvent={calendarEvent} {...props} />;
}

export type CalendarEventModalProps = {
	isLoading?: boolean;
	calendarEvent: ModalCalendarEvent | null | undefined;
	noICalDownload?: boolean;
	noRSVPForm?: boolean;
	isPreview?: boolean;
	withBackButton?: boolean;
	backButtonText?: string;
} & Pick<ModalProps, "ifRef" | "onClose"> &
	Partial<ClassNameProps & StyleProps>;

export function CalendarEventModal({
	isLoading = false,
	calendarEvent,
	noICalDownload = false,
	noRSVPForm = false,
	isPreview = false,
	withBackButton = false,
	backButtonText = "Back to Calendar",
	ifRef,
	onClose,
	className,
	style,
}: CalendarEventModalProps) {
	const isPastEvent = !!calendarEvent && isAfter(new Date(), CalendarEventUtilities.endTimeDate(calendarEvent), "day");

	const viewerResponse = calendarEvent?.calendarEventResponses.find((response) => response.author?.isCurrent) ?? null;

	const { syncID: rsvpSyncID, synchronize: synchronizeRSVP } = useSync();

	return (
		<Modal
			ifRef={ifRef}
			onClose={onClose}
			className={makeClassName(styles["calendar-event-modal"], className)}
			style={style}
		>
			<Modal.Header>
				<Column justify="spaced-start" verticalSpacing="0.25rem" style={{ marginRight: "1.5rem" }}>
					<If condition={withBackButton}>
						{onClose && (
							<Button variant="link" size="small" onClick={onClose}>
								<Row justify="spaced-start" align="center" horizontalSpacing="0.375rem">
									<Icon.ArrowLeft size={14} /> {backButtonText}
								</Row>
							</Button>
						)}
					</If>

					<Row justify="space-between">
						<Heading.H2 noMargin>Event Details</Heading.H2>

						{!!calendarEvent && !noRSVPForm && (
							<If condition={calendarEvent.allowsResponses && !isPastEvent}>
								<div style={{ marginBottom: "-1rem" }}>
									<RSVPForm
										key={rsvpSyncID}
										calendarEventID={calendarEvent.id}
										calendarEventResponse={viewerResponse}
										onSuccess={synchronizeRSVP}
									/>
								</div>
							</If>
						)}
					</Row>
				</Column>
			</Modal.Header>
			<Modal.Body>
				<Conditional>
					<If condition={!calendarEvent && isLoading}>Loading...</If>
					<Else>
						{!!calendarEvent && (
							<React.Fragment>
								<ViewRecorder id={calendarEvent.id} />
								<Conditional>
									<If condition={noRSVPForm}>
										<CalendarEventDetailDisplay
											calendarEvent={calendarEvent}
											withICalDownload={!noICalDownload}
											editable
											isPreview={isPreview}
										/>
									</If>
									<Else>
										<CalendarEventDetailDisplay
											calendarEvent={calendarEvent}
											withICalDownload={!noICalDownload}
											withRSVPForm
											rsvpSyncID={rsvpSyncID}
											synchronizeRSVP={synchronizeRSVP}
											editable
											isPreview={isPreview}
										/>
									</Else>
								</Conditional>
							</React.Fragment>
						)}
					</Else>
				</Conditional>
			</Modal.Body>
		</Modal>
	);
}
